import { CameraIcon, DocumentCheckIcon, SquaresPlusIcon, UserGroupIcon } from '@heroicons/react/20/solid';
import { User } from '../../../types/user';
import { useUserContext } from '../../../providers/UserProvider';
import { useRef, useState } from 'react';
import { useUpdateUsers } from '../../../queries/use-update-users';
import { useIsActiveSubscription } from '../../../hooks/use-is-active-subscription';
import { Menu } from '@headlessui/react';
import { toastNotification } from '../../../components/toast/Toast';
import 'react-image-crop/dist/ReactCrop.css';
import UserRoleBadge from './UserRoleBadge';
import UserAvatar from '../../../components/avatar/UserAvatar';
import FileInput from '../../files/FileInput';
import AvatarColourPickerDialog from './AvatarColourPickerDialog';
import AvatarImagePreview from './AvatarImagePreview';

type UserDetailsProps = {
    user: User;
};

const actions: { [action: string]: string } = {
    CHANGE_AVATAR_COLOUR: 'Change avatar colour',
    CHANGE_PROFILE_PICTURE: 'Change profile picture',
    REMOVE_PROFILE_PICTURE: 'Remove profile picture',
    ADD_PROFILE_PICTURE: 'Add profile picture',
};

function UserDetails({ user }: UserDetailsProps): JSX.Element {
    const { id: currentUserId } = useUserContext();
    const isCurrentUser = currentUserId === user.id;

    const [fileError, setFileError] = useState<string | undefined>(undefined);
    const [showColourPicker, setShowColourPicker] = useState(false);
    const [imgSrc, setImgSrc] = useState<string | undefined>(undefined);
    const uploadRef = useRef<HTMLInputElement | null>(null);

    const activeSubscription = useIsActiveSubscription();
    const updateUsersQuery = useUpdateUsers();

    function openFileInput(): void {
        setFileError(undefined);
        if (uploadRef.current) {
            uploadRef.current.click();
        }
    }

    function handleFileSelect(files: File[]): void {
        const file = files[0];

        setFileError(undefined);

        if (!file) {
            setFileError('A valid file is required.');
            return;
        }

        if (!['image/png', 'image/jpg', 'image/jpeg'].includes(file.type)) {
            setFileError('Invalid file type. File must be .png or .jpg or .jpeg extension.');
            return;
        }

        const fileSize = file.size / (1024 * 1024);
        if (fileSize > 1) {
            setFileError('File size must not be bigger than 1MB.');
            return;
        }

        const reader = new FileReader();
        reader.addEventListener('load', () => {
            setImgSrc(reader.result?.toString() || '');
        });
        reader.readAsDataURL(file);
    }

    function removeAvatar(): void {
        updateUsersQuery.mutate([{ id: currentUserId, fields: { avatarUrl: null } }], {
            onSuccess: () => {
                toastNotification('success', 'Profile avatar successfully removed!');
            },
            onError: () => {
                toastNotification('error', 'Unable to remove profile avatar!');
            },
        });
    }

    function handleAction(action: string): void {
        if (action === 'REMOVE_PROFILE_PICTURE') {
            removeAvatar();
            return;
        }

        if (action === 'CHANGE_PROFILE_PICTURE' || action === 'ADD_PROFILE_PICTURE') {
            openFileInput();
        }

        if (action === 'CHANGE_AVATAR_COLOUR') {
            setShowColourPicker(true);
        }
    }

    return (
        <>
            <FileInput innerRef={uploadRef} onSelect={handleFileSelect} fileTypes={['image/png', 'image/jpg', 'image/jpeg']} />
            {imgSrc && <AvatarImagePreview imgSrc={imgSrc} onClose={(): void => setImgSrc(undefined)} />}
            {showColourPicker && (
                <AvatarColourPickerDialog
                    user={user}
                    defaultColour={user.profileHexColour}
                    onClose={(): void => setShowColourPicker(false)}
                />
            )}
            <div className="border border-gray-200 dark:border-white/[0.07] rounded-lg w-full overflow-hidden pb-4 bg-white dark:bg-gray-800">
                <div className="h-[200px] bg-gradient-to-l from-indigo-400 to-[#9747FF]"></div>
                <div className="grid gap-y-2 px-8">
                    <div className="-mt-[50px] flex-none group relative">
                        <div className="absolute left-0.5 top-0.5">
                            <UserAvatar size="jumbo" ring={true} user={user} />
                            {fileError && <p className="mt-2 text-sm text-red-600">{fileError}</p>}
                        </div>
                        <Menu>
                            <Menu.Button disabled={!activeSubscription}>
                                {isCurrentUser && (
                                    <button
                                        className="hidden group-hover:flex items-center justify-center cursor-pointer disabled:cursor-default z-30 absolute left-0.5 top-0.5 rounded-full w-[100px] h-[100px] bg-gray-800 opacity-70"
                                        disabled={!activeSubscription}
                                    >
                                        <CameraIcon className="w-6 h-6 text-white" />
                                    </button>
                                )}
                            </Menu.Button>
                            <Menu.Items className="py-1 z-40 absolute -left-5 top-[50px] w-max rounded-[5px] border border-gray-300 dark:border-white/[0.07] bg-white dark:bg-gray-800 grid text-xs font-normal text-gray-700 dark:text-gray-200">
                                {Object.keys(actions).map((action, idx) => {
                                    if ((action === 'REMOVE_PROFILE_PICTURE' || action === 'CHANGE_PROFILE_PICTURE') && !user.avatarUrl) {
                                        return <div key={idx}></div>;
                                    }
                                    if ((action === 'CHANGE_AVATAR_COLOUR' || action === 'ADD_PROFILE_PICTURE') && user.avatarUrl) {
                                        return <div key={idx}></div>;
                                    }
                                    return (
                                        <Menu.Button disabled={!activeSubscription} key={idx}>
                                            <button
                                                onClick={(): void => handleAction(action)}
                                                type="button"
                                                disabled={!activeSubscription}
                                                className="w-full focus:outline-none focus:ring-0 disabled:cursor-default disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-white/[0.09] active:bg-gray-100 dark:active:bg-white/[0.11] dark:disabled:bg-white/[0.0]"
                                            >
                                                <span className="flex items-center px-3 py-2">{actions[action]}</span>
                                            </button>
                                        </Menu.Button>
                                    );
                                })}
                            </Menu.Items>
                        </Menu>
                    </div>
                    <div className={`grid gap-y-2 ${fileError ? 'pt-20' : 'pt-14'}`}>
                        <div>
                            <div className="flex gap-2">
                                <h1 className="text-gray-800 dark:text-gray-200 font-medium text-xl">
                                    {user.firstName} {user.lastName}
                                </h1>
                                <UserRoleBadge role={user.role} />
                            </div>
                            {/* <label className="text-gray-500 font-normal text-sm">Senior Accountant</label> */}
                        </div>
                        {/* <div className="flex gap-4 items-center">
                        <div className="flex gap-1 items-center text-sm font-normal text-gray-500">
                            <DocumentCheckIcon className="w-4 h-4" />
                            <p>2 open workpapers</p>
                        </div>
                        <div className="flex gap-1 items-center text-sm font-normal text-gray-500">
                            <UserGroupIcon className="w-4 h-4" />
                            <p>2 teams</p>
                        </div>
                        <div className="flex gap-1 items-center text-sm font-normal text-gray-500">
                            <SquaresPlusIcon className="w-4 h-4" />
                            <p>2 integrations</p>
                        </div>
                    </div> */}
                        {/* <label className="text-gray-500 dark:text-gray-400 text-sm">Senior Accountant</label> */}
                    </div>
                    {/* <div className="flex gap-4 items-center text-gray-500 dark:text-gray-400">
                            <div className="flex gap-1 items-center text-sm">
                                <DocumentCheckIcon className="w-4 h-4" />
                                <p>2 open workpapers</p>
                            </div>
                            <div className="flex gap-1 items-center text-sm">
                                <UserGroupIcon className="w-4 h-4" />
                                <p>2 teams</p>
                            </div>
                            <div className="flex gap-1 items-center text-sm">
                                <SquaresPlusIcon className="w-4 h-4" />
                                <p>2 integrations</p>
                            </div>
                        </div> */}
                </div>
            </div>
        </>
    );
}

export default UserDetails;
